import React, { useMemo } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { cn } from '@lyearn/style';
import { InputBase, Typography } from '@lyearn/ui';
import { DropOverContainer } from '@/components/DragDrop';
import { FormElementProps, FormField } from '@/components/Form';
import useDraggableItem from '@/modules/corporate/components/DraggableList/hooks/useDraggableItem';
import {
  DatabaseFormEditorActionEnum,
  DatabaseFormEditorProps,
  UpdateDatabaseFieldParams,
} from '@/modules/corporate/modules/database/views/FormView/components/DatabaseFormEditor/types';
import SurveyBlockContainer from '@/modules/corporate/modules/survey/views/SurveyEditor/components/SurveyBlockContainer';

import { useDatabaseFormEditor } from '../../context/DatabaseFormEditorContext';
import { baseInputStyles } from '../styles';
import FieldAction from './components/FieldAction';

const CLASSES = { multiline: 'p-0', input: 'p-0 h-auto' };

interface Params {
  fieldId: string;
  onFormEditorAction?: DatabaseFormEditorProps['onFormEditorAction'];
}

const useActions = ({ onFormEditorAction, fieldId }: Params) => {
  return useMemo(() => {
    const onUpdateField = (updates: UpdateDatabaseFieldParams['updates']) => {
      onFormEditorAction?.({
        type: DatabaseFormEditorActionEnum.UpdateField,
        payload: { fieldId, updates },
      });
    };

    return {
      onNameChange: (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        const label = event.target.value;
        onUpdateField({ viewField: { label }, metadataField: { name: label } });
      },
      onDescriptionChange: (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        onUpdateField({ viewField: { description: event.target.value } });
      },
      onSelectField: () => {
        onFormEditorAction?.({
          type: DatabaseFormEditorActionEnum.SelectField,
          payload: { fieldId },
        });
      },
      onFieldAction: (actionType: string) => {
        onFormEditorAction?.({
          type: actionType as DatabaseFormEditorActionEnum.DuplicateField,
          payload: { fieldId },
        });
      },
    };
  }, [fieldId, onFormEditorAction]);
};

function FormEditorField(props: FormElementProps) {
  const { t } = useTranslation();
  const { field, ...fieldProps } = props;
  const {
    isFieldPublished,
    errors,
    draggingItemRef,
    getFieldAttributes,
    selectedFieldId,
    onDrop,
    onFormEditorAction,
  } = useDatabaseFormEditor();

  const fieldId = field.name;
  const error = errors?.[fieldId];
  const isSelected = fieldId === selectedFieldId;
  const actions = useActions({ onFormEditorAction, fieldId });

  const fieldAttributes = getFieldAttributes(fieldId);

  const { getDragHandleProps, getDropOverContainerProps } = useDraggableItem({
    key: fieldAttributes.index,
    draggingItemRef,
    onDrop,
  });

  const adaptedField = useMemo(() => {
    const descriptionEnabled = fieldAttributes.descriptionEnabled;

    return {
      ...field,
      label: (
        <InputBase
          multiline
          className={cn('w-full', baseInputStyles)}
          classes={CLASSES}
          placeholder={t('Database:WriteYourQuestion')}
          readOnly={!isSelected}
          value={field.label}
          onChange={actions.onNameChange}
        />
      ),
      description: descriptionEnabled ? (
        <InputBase
          multiline
          className={cn('w-full text-text-secondary', baseInputStyles)}
          classes={CLASSES}
          placeholder={t('Common:Form.Description')}
          readOnly={!isSelected}
          value={field.description}
          onChange={actions.onDescriptionChange}
        />
      ) : null,
    };
  }, [
    actions.onDescriptionChange,
    actions.onNameChange,
    field,
    fieldAttributes.descriptionEnabled,
    isSelected,
    t,
  ]);

  return (
    <>
      <DropOverContainer className="group px-40" {...getDropOverContainerProps()}>
        <FieldAction
          allowPartialUpdates={isFieldPublished(fieldId)}
          getDragHandleProps={getDragHandleProps}
          onAction={actions.onFieldAction}
        />
        <SurveyBlockContainer
          className="my-4"
          isSelected={isSelected}
          onSelect={actions.onSelectField}>
          <FormField field={adaptedField} {...fieldProps} />
        </SurveyBlockContainer>
        {error ? (
          <Typography
            className="m-8 mt-4"
            color="text-text-error"
            data-database-form-error={error.message}
            variant="body-short-02">
            {error.message}
          </Typography>
        ) : null}
      </DropOverContainer>
    </>
  );
}

export default FormEditorField;
